import { useParams, Navigate } from "react-router-dom";
import { PageHero } from "@/components/site/Sections";
import { useSettings } from "@/hooks/useSettings";

const CONTENT = {
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro:
      "This Privacy Policy explains how United Youngs India (UYI) collects, uses and protects the information you share with us through this website.",
    sections: [
      { h: "Information we collect", p: "When you submit an enquiry or contact form, we collect your name, phone number, email address and any project details you choose to share. We may also collect basic, non-identifying analytics such as pages visited and device type." },
      { h: "How we use your information", p: "We use your details solely to respond to your enquiry, prepare estimates, schedule consultations and improve our services. We do not sell or rent your personal information to any third party." },
      { h: "Data storage & security", p: "Enquiries are stored securely and accessed only by authorised UYI team members. We take reasonable technical and organisational measures to protect your data against unauthorised access." },
      { h: "Cookies", p: "This website may use minimal cookies or local storage to keep the site functioning smoothly. You can disable cookies in your browser settings at any time." },
      { h: "Third-party links", p: "Our website may link to external platforms such as Google, Instagram, Facebook or YouTube. We are not responsible for the privacy practices of those websites." },
      { h: "Your rights", p: "You may request access to, correction of, or deletion of your personal information at any time by contacting us using the details below." },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms & Conditions",
    intro:
      "By accessing and using the United Youngs India website, you agree to the following terms and conditions.",
    sections: [
      { h: "Use of website", p: "The content on this website is provided for general information about UYI's construction, interior and furniture services. You agree to use the website lawfully and not to misuse any content or functionality." },
      { h: "Services & estimates", p: "All quotations, timelines and designs shared through this website are indicative. Final scope, pricing and delivery are confirmed only through a signed agreement between you and UYI." },
      { h: "Intellectual property", p: "All logos, text, images and designs on this website are the property of United Youngs India unless otherwise stated, and may not be reproduced without written permission." },
      { h: "Project imagery", p: "Portfolio and gallery images represent our style and capability. Some visuals may be representative or for illustration purposes." },
      { h: "Limitation of liability", p: "UYI is not liable for any indirect or consequential loss arising from the use of this website. Engagement of our services is governed by the specific project contract." },
      { h: "Governing law", p: "These terms are governed by the laws of India, with jurisdiction in the courts of Lucknow, Uttar Pradesh." },
    ],
  },
  disclaimer: {
    eyebrow: "Legal",
    title: "Disclaimer",
    intro:
      "The information provided on this website is for general informational purposes only. All information is provided in good faith.",
    sections: [
      { h: "No professional advice", p: "Content on this website does not constitute engineering, architectural, financial or legal advice. Always consult with our team for guidance specific to your project." },
      { h: "Accuracy of information", p: "While we strive to keep information current and correct, UYI makes no warranties of any kind, express or implied, about the completeness or accuracy of the website content." },
      { h: "External images & references", p: "Certain images may be representative and used for illustrative purposes only, and may not depict actual completed projects." },
      { h: "External links", p: "Through this website you may link to other sites that are not under our control. We have no responsibility for the nature and content of those sites." },
      { h: "Consent", p: "By using our website, you consent to this disclaimer and agree to its terms." },
    ],
  },
};

export default function Legal() {
  const { doc } = useParams();
  const data = CONTENT[doc];
  const s = useSettings();
  if (!data) return <Navigate to="/" replace />;

  return (
    <div data-testid={`legal-${doc}`}>
      <PageHero eyebrow={data.eyebrow} title={data.title} subtitle={data.intro} />
      <section className="mx-auto max-w-3xl px-5 lg:px-8 py-20 lg:py-28">
        <div className="space-y-12">
          {data.sections.map((sec, i) => (
            <div key={i} className="border-t border-navy/10 pt-6">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-gold text-xl">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl text-navy">{sec.h}</h2>
              </div>
              <p className="mt-4 md:pl-10 text-navy/60 leading-relaxed">{sec.p}</p>
            </div>
          ))}
          <div className="border-t border-navy/10 pt-6">
            <h2 className="font-display text-2xl text-navy">Contact us</h2>
            <p className="mt-4 text-navy/60 leading-relaxed">
              For any questions about this page, reach us at{" "}
              <span className="text-navy">{s.email}</span> · {s.phone_primary}
              <br />
              {s.address}
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-navy/40">
              Last updated · {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
